// ---- Hold seating — pure grip/landmark math (DOM-free) ----
// The contract for every held item (weapon / shield / fishing rod):
//
//   model.landmarks.grip  ==  item hold.hilt
//   seatWeapon pins that point to the hand socket origin for ANY rot
//   offset is a tiny post-alignment nudge (prefer 0)
//   secondary landmarks (string/tip/head/guard) must sit FARTHER from the fist
//   than grip after seating — otherwise the wrong part is "in" the hand
//
// Orientation layers (ONE source of truth for the base seat):
//   1. item hold.rot     — THE base orientation in the fist
//   2. clips hold override — rare; only when anim truly needs a different base
//   3. stance/clip grip  — small DELTA on rot for one pose (slash lay-forward),
//                          never a full re-seat
//
// Used by content validate + inspect-model; kept free of THREE scene graph so
// validate stays dependency-light. Quaternion math matches THREE.Euler XYZ +
// seatWeapon (src/anim/pose.ts).
//
// Secondary-landmark distance under hilt===grip and offset≈0 is ROT-INVARIANT
// (|R*(pt−grip)| = |pt−grip|). That check catches wrong hilt, large offset that
// leaves a secondary nearer the fist, or landmarks placed on top of grip — not
// a bad rot alone.

export type Vec3 = [number, number, number];

export type HoldLike = {
  hilt: Vec3;
  rot: Vec3;
  scale?: number;
  offset?: Vec3;
};

/** Known landmark names. `grip` is required for held models; others optional. */
export type LandmarkName = "grip" | "string" | "tip" | "head" | "guard";

export type Landmarks = Partial<Record<LandmarkName, Vec3>>;

/** |offset| above this → soft warn (2H diagonal carry may exceed it intentionally). */
export const OFFSET_WARN = 0.08;
/** |offset| above this → hard error (real grip is no longer in the fist). */
export const OFFSET_ERR = 0.45;
/** hilt must match landmarks.grip within this epsilon. */
export const HILT_EPS = 1e-4;
/** Secondary landmark must be at least this much farther from the fist than grip. */
export const SECONDARY_MARGIN = 0.04;

const SECONDARY: LandmarkName[] = ["string", "tip", "head", "guard"];

// ---- vec / quat (THREE.Euler XYZ → quaternion, same as seatWeapon) ----

function len(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function mulS(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

function near(a: Vec3, b: Vec3, eps = HILT_EPS): boolean {
  return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps && Math.abs(a[2] - b[2]) <= eps;
}

/** THREE.Euler(x,y,z,'XYZ') → quaternion [x,y,z,w]. */
function eulerXYZToQuat(e: Vec3): [number, number, number, number] {
  const cx = Math.cos(e[0] / 2), sx = Math.sin(e[0] / 2);
  const cy = Math.cos(e[1] / 2), sy = Math.sin(e[1] / 2);
  const cz = Math.cos(e[2] / 2), sz = Math.sin(e[2] / 2);
  return [
    sx * cy * cz + cx * sy * sz,
    cx * sy * cz - sx * cy * sz,
    cx * cy * sz + sx * sy * cz,
    cx * cy * cz - sx * sy * sz,
  ];
}

function rotateByQuat(v: Vec3, q: [number, number, number, number]): Vec3 {
  const [qx, qy, qz, qw] = q;
  // q * (0,v) * q^-1
  const ix = qw * v[0] + qy * v[2] - qz * v[1];
  const iy = qw * v[1] + qz * v[0] - qx * v[2];
  const iz = qw * v[2] + qx * v[1] - qy * v[0];
  const iw = -qx * v[0] - qy * v[1] - qz * v[2];
  return [
    ix * qw + iw * -qx + iy * -qz - iz * -qy,
    iy * qw + iw * -qy + iz * -qx - ix * -qz,
    iz * qw + iw * -qz + ix * -qy - iy * -qx,
  ];
}

/**
 * Socket-local position of a model-local landmark after `seatWeapon` (no arm
 * counter-rotate). Matches pose.ts:
 *   pos = R*(local*scale) - R*(hilt*scale) + offset
 *       = R*(local - hilt)*scale + offset
 */
export function seatLocalPoint(local: Vec3, hold: HoldLike): Vec3 {
  const s = hold.scale ?? 1;
  const q = eulerXYZToQuat(hold.rot);
  const delta = mulS(sub(local, hold.hilt), s);
  const rotated = rotateByQuat(delta, q);
  const off = hold.offset ?? ([0, 0, 0] as Vec3);
  return add(rotated, off);
}

/** Distance from the hand socket origin to a seated landmark. */
export function landmarkDistToSocket(local: Vec3, hold: HoldLike): number {
  return len(seatLocalPoint(local, hold));
}

export type HoldSeatIssue = {
  level: "error" | "warn";
  path: string;
  message: string;
};

/**
 * Structural seat check (no pose, no scene): grip lock + offset hygiene +
 * secondary landmarks farther from the fist than grip.
 */
export function checkHoldSeat(
  hold: HoldLike,
  landmarks: Landmarks | undefined,
  opts?: { pathPrefix?: string },
): HoldSeatIssue[] {
  const p = opts?.pathPrefix ?? "hold";
  const issues: HoldSeatIssue[] = [];
  const grip = landmarks?.grip;

  if (!grip) {
    issues.push({
      level: "error",
      path: "landmarks.grip",
      message:
        "held model needs landmarks.grip (the handle center in model local space) — item hold.hilt must match it",
    });
    return issues;
  }

  if (!near(hold.hilt, grip)) {
    issues.push({
      level: "error",
      path: `${p}.hilt`,
      message:
        `hold.hilt [${hold.hilt}] must equal model landmarks.grip [${grip}] (the seat point) — set both to the handle center`,
    });
  }

  const off = hold.offset ?? ([0, 0, 0] as Vec3);
  const offLen = len(off);
  if (offLen > OFFSET_ERR) {
    issues.push({
      level: "error",
      path: `${p}.offset`,
      message:
        `|offset|=${offLen.toFixed(3)} pulls the grip far off the fist (max ${OFFSET_ERR}) — fix rot/hilt instead of offsetting`,
    });
  } else if (offLen > OFFSET_WARN) {
    issues.push({
      level: "warn",
      path: `${p}.offset`,
      message:
        `|offset|=${offLen.toFixed(3)} is large (prefer ≤${OFFSET_WARN}) — only 2H diagonal carry should need a big nudge`,
    });
  }

  // Grip at the socket after seating = |offset|. Secondary landmarks must be farther.
  // Note: with hilt===grip and offset≈0 this distance is rot-invariant (see header).
  const gripDist = landmarkDistToSocket(grip, hold);
  for (const name of SECONDARY) {
    const pt = landmarks?.[name];
    if (!pt) continue;
    const d = landmarkDistToSocket(pt, hold);
    if (d + 1e-6 < gripDist + SECONDARY_MARGIN) {
      issues.push({
        level: "error",
        path: `landmarks.${name}`,
        message:
          `${name} is closer to the fist (dist ${d.toFixed(3)}) than grip (dist ${gripDist.toFixed(3)}) after seating — check hold.hilt=landmarks.grip, offset, and landmark placement (rot alone cannot separate them when offset is 0)`,
      });
    }
  }

  return issues;
}

/** Merge item hold with an optional clips hold override (hilt never overridden). */
export function mergeHold(base: HoldLike, over?: Partial<Omit<HoldLike, "hilt">> | null): HoldLike {
  if (!over) return base;
  return {
    hilt: base.hilt,
    rot: over.rot ?? base.rot,
    scale: over.scale ?? base.scale,
    offset: over.offset ?? base.offset,
  };
}

/** |grip| euler magnitude — large stance/clip grip deltas are a smell (full re-seat).
 *  ~1.5 allows a modest ready residual after baking carry into hold.rot (2H weapons). */
export const GRIP_DELTA_WARN = 1.55;

/**
 * 2H **carry** may need a larger grip delta (tip-up trail with raw seating) after
 * hold is baked for ready. Still warn past this — prefer modest, not a full re-seat.
 */
export const GRIP_DELTA_WARN_2H_CARRY = 2.2;

export function gripDeltaMagnitude(grip: Vec3 | undefined): number {
  if (!grip) return 0;
  return len(grip);
}

/** |hold.offset| (0 when omitted) — grip sits this far from the socket after seatWeapon. */
export function holdOffsetLen(hold: HoldLike): number {
  return len(hold.offset ?? ([0, 0, 0] as Vec3));
}
